'use client';

import Link from 'next/link';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Navigation from '../../../components/Navigation';
import RelatedPages from '../../../components/RelatedPages';
import { GuideEyebrow, GuideSection, GuideSubtitle, GuideTitle } from '../../../components/GuideHeading';

const Code = ({ children }: { children: string }) => (
    <Box
        component="pre"
        sx={{
            backgroundColor: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            p: 2,
            mb: 3,
            overflow: 'auto',
            fontFamily: 'monospace',
            fontSize: '0.85rem',
            lineHeight: 1.6,
        }}
    >
        {children}
    </Box>
);

export default function JsonPatchInJavaGuide() {
    return (
        <>
            <Navigation activePage="docs" />
            <main>
                <Container maxWidth="lg" className="main-container">
                    <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
                        <GuideEyebrow>Guide</GuideEyebrow>
                        <GuideTitle>JSON Patch in Java</GuideTitle>
                        <GuideSubtitle>
                            How to apply and generate RFC 6902 JSON Patches in Java with the java-json-tools library on top of Jackson - with working code you can copy straight into a project.
                        </GuideSubtitle>

                        <GuideSection>Installing the library</GuideSection>
                        <Typography sx={{ color: 'var(--fg-muted)', mb: 2 }}>
                            The reference implementation is <code>java-json-tools/json-patch</code>, built on Jackson. It implements every RFC 6902 operation - add, remove, replace, move, copy and test:
                        </Typography>
                        <Code>{`<!-- Maven -->
<dependency>
    <groupId>com.github.java-json-tools</groupId>
    <artifactId>json-patch</artifactId>
    <version>1.13</version>
</dependency>

// Gradle
implementation 'com.github.java-json-tools:json-patch:1.13'`}</Code>

                        <GuideSection>Applying a JSON Patch</GuideSection>
                        <Typography sx={{ color: 'var(--fg-muted)', mb: 2 }}>
                            Parse both the document and the patch with Jackson's <code>ObjectMapper</code>, build a <code>JsonPatch</code>, and <code>apply</code> it. The result is a new <code>JsonNode</code>:
                        </Typography>
                        <Code>{`import com.fasterxml.jackson.databind.JsonNode;
import com.fasterxml.jackson.databind.ObjectMapper;
import com.github.fge.jsonpatch.JsonPatch;

ObjectMapper mapper = new ObjectMapper();

JsonNode doc = mapper.readTree(
    "{\\"name\\": \\"demo\\", \\"version\\": 1, \\"tags\\": [\\"a\\", \\"b\\"]}"
);

JsonNode patchJson = mapper.readTree(
    "[{\\"op\\": \\"replace\\", \\"path\\": \\"/version\\", \\"value\\": 2},"
  + " {\\"op\\": \\"add\\", \\"path\\": \\"/tags/-\\", \\"value\\": \\"c\\"},"
  + " {\\"op\\": \\"remove\\", \\"path\\": \\"/name\\"}]"
);

JsonPatch patch = JsonPatch.fromJson(patchJson);
JsonNode result = patch.apply(doc);

System.out.println(mapper.writeValueAsString(result));
// {"tags":["a","b","c"],"version":2}`}</Code>
                        <Typography sx={{ color: 'var(--fg-muted)', mb: 3 }}>
                            Note the special path <code>/tags/-</code>: the dash appends to the end of an array. Paths follow JSON Pointer (RFC 6901), so <code>~</code> and <code>/</code> inside keys are escaped as <code>~0</code> and <code>~1</code>.
                        </Typography>

                        <GuideSection>Generating a patch from two documents</GuideSection>
                        <Typography sx={{ color: 'var(--fg-muted)', mb: 2 }}>
                            <code>JsonDiff.asJsonPatch</code> diffs two <code>JsonNode</code> documents and returns the RFC 6902 operations as a JSON array - the same thing the{' '}
                            <Link href="/" style={{ color: 'var(--accent)' }}>JSON Patch Generator</Link> does in your browser:
                        </Typography>
                        <Code>{`import com.github.fge.jsonpatch.diff.JsonDiff;

JsonNode original = mapper.readTree("{\\"name\\": \\"demo\\", \\"version\\": 1}");
JsonNode modified = mapper.readTree(
    "{\\"name\\": \\"demo\\", \\"version\\": 2, \\"tags\\": [\\"a\\"]}"
);

JsonNode patchJson = JsonDiff.asJsonPatch(original, modified);

System.out.println(mapper.writeValueAsString(patchJson));
// [{"op":"replace","path":"/version","value":2},{"op":"add","path":"/tags","value":["a"]}]`}</Code>

                        <GuideSection>Handling test failures and errors</GuideSection>
                        <Typography sx={{ color: 'var(--fg-muted)', mb: 2 }}>
                            Two different things can go wrong. <code>JsonPatch.fromJson</code> throws <code>JsonPatchException</code> when the patch document itself is invalid - unknown operation, missing <code>path</code>, malformed JSON Pointer. <code>patch.apply</code> throws <code>JsonPatchException</code> when a well-formed patch does not fit the document - a missing path, an array index out of range, or a <code>test</code> operation whose value does not match:
                        </Typography>
                        <Code>{`try {
    JsonPatch patch = JsonPatch.fromJson(patchJson);
    JsonNode result = patch.apply(doc);
    return result;
} catch (JsonPatchException e) {
    // malformed patch OR rejected by the document
    // (a failed test operation lands here - HTTP 412 territory)
    throw new IllegalArgumentException("JSON Patch failed: " + e.getMessage(), e);
}`}</Code>
                        <Typography sx={{ color: 'var(--fg-muted)', mb: 3 }}>
                            RFC 6902 stops evaluation at the first failed operation. Rollback of earlier mutations is implementation-specific, so verify the library before treating a patch as a transaction.
                        </Typography>

                        <GuideSection>Common pitfalls</GuideSection>
                        <Typography component="ul" sx={{ color: 'var(--fg-muted)', mb: 3, pl: 3 }}>
                            <li><strong>Evaluation stops on error.</strong> RFC 6902 does not guarantee rollback of earlier operations; handle exceptions and verify this library&apos;s behavior before assuming transaction-like semantics.</li>
                            <li><strong>Numbers are not strings.</strong> A test on <code>{'{"value": 1}'}</code> fails against <code>{'{"value": "1"}'}</code>. Type mismatches are the most common source of failed test operations, and Jackson makes them easy to miss: keep your <code>JsonNode</code> types (IntNode, DoubleNode, TextNode) consistent between patch and document.</li>
                            <li><strong>Array indices shift.</strong> Removing index 0 makes index 1 become index 0. Order remove operations from the highest index down, or use value-based test guards.</li>
                            <li><strong>JsonNode is immutable.</strong> <code>apply</code> returns a new tree; assign the result. To map it back to a POJO, use <code>mapper.treeToValue(result, MyClass.class)</code>.</li>
                        </Typography>

                        <GuideSection>Try it in the browser first</GuideSection>
                        <Typography sx={{ color: 'var(--fg-muted)', mb: 2 }}>
                            Before writing Java, prototype the patch visually. Paste your two documents into the{' '}
                            <Link href="/" style={{ color: 'var(--accent)' }}>JSON Patch Generator</Link> to get the exact RFC 6902 operations, or use the{' '}
                            <Link href="/diff" style={{ color: 'var(--accent)' }}>JSON Diff tool</Link> to see every changed line. Then drop the generated patch into <code>patch.apply</code>.
                        </Typography>
                        <Typography sx={{ color: 'var(--fg-muted)', mb: 4 }}>
                            New to the format? Read the <Link href="/docs" style={{ color: 'var(--accent)' }}>JSON Patch documentation</Link> or browse{' '}
                            <Link href="/examples" style={{ color: 'var(--accent)' }}>real-world examples</Link>.
                        </Typography>

                        <RelatedPages currentPage="docs" />
                    </Box>
                </Container>
            </main>
        </>
    );
            }
